import React, { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const isSuperAdminPath = () => window.location.pathname.replace(/\/+$/, '') === '/superadmin';

// ─── Auth Context ─────────────────────────────────────────────────────────────
interface User { id: number; email: string; full_name: string; role: string; company_id: number | null; }
interface AuthCtx { user: User | null; token: string | null; login: (e: string, p: string) => Promise<void>; logout: () => void; }
const AuthContext = createContext<AuthCtx>({} as AuthCtx);
const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => { try { return JSON.parse(localStorage.getItem('si_admin_user') || 'null'); } catch { return null; } });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('si_admin_token'));
  const login = async (email: string, password: string) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (!['admin', 'super_admin'].includes(res.data.user.role)) throw new Error('Acces permis doar adminilor');
    if (res.data.user.role === 'super_admin' && !isSuperAdminPath()) throw new Error('Acces super admin disponibil doar pe /superadmin');
    if (res.data.user.role === 'admin' && isSuperAdminPath()) throw new Error('Acces permis doar super adminului');
    setToken(res.data.access_token); setUser(res.data.user);
    localStorage.setItem('si_admin_token', res.data.access_token);
    localStorage.setItem('si_admin_user', JSON.stringify(res.data.user));
  };
  const logout = () => {
    setToken(null); setUser(null);
    localStorage.removeItem('si_admin_token'); localStorage.removeItem('si_admin_user');
    localStorage.removeItem('selectedCompanyId'); localStorage.removeItem('selectedCompanyName');
  };
  return <AuthContext.Provider value={{ user, token, login, logout }}>{children}</AuthContext.Provider>;
}

function useApi() {
  const { token } = useAuth();
  return useMemo(
    () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );
}

function usePasswordChange() {
  const api = useApi();
  return async () => {
    const current = window.prompt('Parola curentă');
    if (!current) return;
    const next = window.prompt('Parola nouă (minim 6 caractere)');
    if (!next) return;
    const confirm = window.prompt('Confirmă parola nouă');
    if (next !== confirm) {
      window.alert('Parolele nu coincid.');
      return;
    }
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      window.alert('Parola a fost schimbată.');
    } catch (err: any) {
      window.alert(err.response?.data?.detail || 'Nu am putut schimba parola.');
    }
  };
}

// ─── Shared styles ────────────────────────────────────────────────────────────
const S = {
  card: { background: '#111118', border: '1px solid #1a1a25', borderRadius: 12, padding: '20px 24px' } as React.CSSProperties,
  label: { display: 'block', color: '#666', fontSize: 11, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase' as const, marginBottom: 6 },
  input: { width: '100%', background: '#0a0a0f', border: '1px solid #2a2a35', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'inherit' },
  btn: (color = '#6366f1') => ({ background: color, border: 'none', borderRadius: 8, padding: '9px 18px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' } as React.CSSProperties),
  btnGhost: { background: 'none', border: '1px solid #222', borderRadius: 8, padding: '8px 16px', color: '#888', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' } as React.CSSProperties,
};

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState(''); const [password, setPassword] = useState('');
  const [error, setError] = useState(''); const [loading, setLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await login(email, password); }
    catch (err: any) { setError(err.response?.data?.detail || err.message || 'Eroare'); }
    finally { setLoading(false); }
  };
  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <div style={{ width: 360 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg, #f59e0b, #ef4444)', borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
          </div>
          <div style={{ color: '#fff', fontSize: 18, fontWeight: 600 }}>Admin Panel</div>
          <div style={{ color: '#555', fontSize: 12, marginTop: 4 }}>Sales Intelligence · TPC Concept</div>
        </div>
        <form onSubmit={handleSubmit} style={{ ...S.card, padding: 28 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={S.label}>Email</label>
            <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="calin@tpcconcept.ro" required />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={S.label}>Parolă</label>
            <input style={S.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required />
          </div>
          {error && <div style={{ background: '#2a0a0a', border: '1px solid #5a1a1a', borderRadius: 8, padding: '9px 12px', color: '#f87171', fontSize: 12, marginBottom: 14 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ ...S.btn('linear-gradient(135deg, #f59e0b, #ef4444)' as any), width: '100%', padding: '10px 0' }}>
            {loading ? 'Se autentifică...' : 'Intră în Admin'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── NAV ──────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'company', label: 'Companie', icon: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z' },
  { id: 'uploads', label: 'Fișiere & Calcul', icon: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12' },
  { id: 'users', label: 'Utilizatori', icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75' },
  { id: 'summary', label: 'Statistici', icon: 'M18 20V10M12 20V4M6 20v-6' },
];

// ─── COMPANY OVERVIEW TAB ────────────────────────────────────────────────────
function CompanyTab() {
  const api = useApi();
  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/admin/company')
      .then(r => { setCompany(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [api]);

  const fmtRON = (v: number | null | undefined) =>
    v != null ? new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(v) + ' RON' : '—';

  if (loading) return <div style={{ ...S.card, textAlign: 'center', padding: 60, color: '#444' }}>Se încarcă...</div>;
  if (!company) return <div style={{ ...S.card, textAlign: 'center', padding: 60, color: '#444' }}>Nu am găsit compania asociată contului.</div>;

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div style={S.card}>
        <div style={{ color: '#fff', fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{company.name}</div>
        <div style={{ color: '#555', fontSize: 13, fontFamily: "'DM Mono', monospace" }}>
          {company.slug} · @{company.allowed_domain || 'domeniu neconfigurat'}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
        {[
          { label: 'Agenți', value: company.agents_count ?? 0 },
          { label: 'Utilizatori', value: company.users_count ?? 0 },
          { label: 'Vânzări', value: fmtRON(company.total_sales) },
          { label: 'Articole', value: company.total_articles ?? 0 },
          { label: 'Potențial P1', value: company.total_recs_p1 ?? 0 },
        ].map(s => (
          <div key={s.label} style={{ background: '#111118', border: '1px solid #1a1a25', borderRadius: 8, padding: '16px 18px' }}>
            <div style={{ color: '#555', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginBottom: 6 }}>{s.label}</div>
            <div style={{ color: '#fff', fontSize: 20, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{s.value}</div>
          </div>
        ))}
      </div>
      <div style={S.card}>
        <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Status date</div>
        <div style={{ color: '#666', fontSize: 13 }}>
          {company.last_calculated_at
            ? `Ultimul calcul: ${new Date(company.last_calculated_at).toLocaleString('ro-RO')}`
            : 'Încă nu există un calcul finalizat pentru această companie.'}
        </div>
      </div>
    </div>
  );
}

// ─── UPLOADS & CALCULATE TAB ──────────────────────────────────────────────────
function UploadsTab({
  companyId, suggestion, setSuggestion, appliedConfig, setAppliedConfig,
  calcResult, setCalcResult, aiLoading, setAiLoading,
}: {
  companyId: number | null;
  suggestion: any; setSuggestion: (v: any) => void;
  appliedConfig: any; setAppliedConfig: (v: any) => void;
  calcResult: any; setCalcResult: (v: any) => void;
  aiLoading: boolean; setAiLoading: (v: boolean) => void;
}) {
  const api = useApi();
  const [uploads, setUploads] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [msg, setMsg] = useState('');
  const [applyingCluster, setApplyingCluster] = useState(false);
  const [applyMsg, setApplyMsg] = useState('');
  const [editableSuggestion, setEditableSuggestion] = useState<any>(null);
  const [newSeg, setNewSeg] = useState({ name: '', col1: '', col2: '' });
  const [showAddSeg, setShowAddSeg] = useState(false);
  const [colValues, setColValues] = useState<Record<string, string[]>>({});

  const fetchUploads = useCallback(() => {
    if (!companyId) return;
    api.get(`/admin/uploads?company_id=${companyId}`).then(r => setUploads(r.data)).catch(() => {});
  }, [companyId, api]);

  useEffect(() => { fetchUploads(); }, [fetchUploads]);

  useEffect(() => {
    if (!suggestion) { setEditableSuggestion(null); return; }
    setEditableSuggestion({
      ...suggestion,
      top_segments: [...(suggestion.top_segments || [])].sort(
        (a: any, b: any) => (b.estimated_clients || 0) - (a.estimated_clients || 0)
      ),
    });
  }, [suggestion]);

  const sortSegs = (segs: any[]) =>
    [...segs].sort((a: any, b: any) => (b.estimated_clients || 0) - (a.estimated_clients || 0));

  const deleteSegment = (idx: number) =>
    setEditableSuggestion((prev: any) => ({
      ...prev,
      top_segments: prev.top_segments.filter((_: any, i: number) => i !== idx),
    }));

  useEffect(() => {
    if (!showAddSeg || !companyId || !editableSuggestion?.recommended_columns) return;
    editableSuggestion.recommended_columns.forEach((col: string) => {
      api.get(`/admin/column-values/${companyId}?column=${encodeURIComponent(col)}`)
        .then(r => setColValues(prev => ({ ...prev, [col]: r.data.values || [] })))
        .catch(() => {});
    });
  }, [showAddSeg]);  // eslint-disable-line react-hooks/exhaustive-deps

  const addSegment = () => {
    if (!newSeg.name.trim()) return;
    const col1Name = editableSuggestion?.recommended_columns?.[0];
    const col2Name = editableSuggestion?.recommended_columns?.[1];
    const filters: Record<string, string> = {};
    if (col1Name && newSeg.col1) filters[col1Name] = newSeg.col1;
    if (col2Name && newSeg.col2) filters[col2Name] = newSeg.col2;
    setEditableSuggestion((prev: any) => ({
      ...prev,
      top_segments: sortSegs([...prev.top_segments, {
        name: newSeg.name, filters, estimated_clients: 0, business_insight: '',
      }]),
    }));
    setNewSeg({ name: '', col1: '', col2: '' });
    setShowAddSeg(false);
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || !companyId) return;
    setUploading(true); setMsg('');
    for (const file of Array.from(files)) {
      const form = new FormData();
      form.append('file', file);
      form.append('company_id', String(companyId));
      try {
        await api.post('/admin/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        setMsg(`✓ ${file.name} uploadat`);
      } catch (err: any) { setMsg(`✗ ${file.name}: ${err.response?.data?.detail || 'Eroare'}`); }
    }
    setUploading(false); fetchUploads();
  };

  const deleteUpload = async (id: number) => {
    try { await api.delete(`/admin/uploads/${id}`); fetchUploads(); }
    catch {}
  };

  const calculate = async () => {
    if (!companyId) return;
    if (!appliedConfig) { setMsg('Aplică mai întâi configurația AI înainte de calcul.'); return; }
    setCalculating(true); setCalcResult(null); setMsg('');
    try {
      const res = await api.post('/admin/calculate', { company_id: companyId, category_columns: ['specialization', 'surface_size'] });
      setCalcResult(res.data); setMsg('');
    } catch (err: any) { setMsg(err.response?.data?.detail || 'Eroare la calcul'); }
    finally { setCalculating(false); }
  };

  const suggestClustering = async () => {
    if (!companyId) return;
    setAiLoading(true); setSuggestion(null); setApplyMsg('');
    try {
      const res = await api.post('/admin/suggest-clustering', { company_id: companyId, company_context: '' });
      setSuggestion(res.data.suggestion);
    } catch (err: any) { setMsg(err.response?.data?.detail || 'Eroare la sugestie AI'); }
    finally { setAiLoading(false); }
  };

  const applyClusterConfig = async () => {
    if (!companyId || !editableSuggestion) return;
    setApplyingCluster(true); setApplyMsg('');
    try {
      await api.post('/admin/apply-clustering', {
        company_id: companyId,
        category_columns: editableSuggestion.recommended_columns,
        min_clients: editableSuggestion.min_clients_threshold,
        custom_segments: editableSuggestion.top_segments
          .filter((seg: any) => seg.filters && Object.keys(seg.filters).length > 0)
          .map((seg: any) => ({ name: seg.name, filters: seg.filters })),
      });
      setAppliedConfig(editableSuggestion);
      setApplyMsg('✓ Configurație aplicată — acum poți calcula');
      setSuggestion(null);
    } catch (err: any) { setApplyMsg(err.response?.data?.detail || 'Eroare la aplicare'); }
    finally { setApplyingCluster(false); }
  };

  if (!companyId) return (
    <div style={{ ...S.card, textAlign: 'center', padding: 60 }}>
      <div style={{ color: '#444', fontSize: 14 }}>Selectează o companie din tab-ul Companii înainte de upload.</div>
    </div>
  );

  const FILE_TYPE_COLOR: Record<string, string> = { transactions: '#6366f1', articles: '#10b981', clients: '#f59e0b', unknown: '#666' };
  const FILE_TYPE_LABEL: Record<string, string> = { transactions: 'Tranzacții', articles: 'Articole', clients: 'Clienți', unknown: 'Necunoscut' };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        style={{
          border: `2px dashed ${dragOver ? '#6366f1' : '#2a2a35'}`,
          borderRadius: 12, padding: '40px 24px', textAlign: 'center',
          background: dragOver ? '#1a1a2e' : '#0d0d14', cursor: 'pointer', transition: 'all 0.2s',
        }}
        onClick={() => document.getElementById('file-input')?.click()}
      >
        <input id="file-input" type="file" accept=".xlsx,.xls" multiple style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)} />
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#444" strokeWidth="1.5" style={{ marginBottom: 12 }}>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        <div style={{ color: '#888', fontSize: 14, fontWeight: 500 }}>
          {uploading ? 'Se uploadează...' : 'Trage fișierele Excel aici sau click pentru selectare'}
        </div>
        <div style={{ color: '#444', fontSize: 12, marginTop: 6 }}>Suportă: fișier tranzacții + fișier articole (opțional)</div>
      </div>

      {msg && <div style={{ background: msg.startsWith('✓') ? '#0a1f0a' : '#1f0a0a', border: `1px solid ${msg.startsWith('✓') ? '#1a4a1a' : '#4a1a1a'}`, borderRadius: 8, padding: '10px 14px', color: msg.startsWith('✓') ? '#4ade80' : '#f87171', fontSize: 13 }}>{msg}</div>}

      {/* Uploaded files list */}
      {uploads.length > 0 && (
        <div style={S.card}>
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Fișiere uploadate ({uploads.length})</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {uploads.map(u => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#0a0a0f', borderRadius: 8, border: '1px solid #1a1a25' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ background: FILE_TYPE_COLOR[u.file_type] + '22', color: FILE_TYPE_COLOR[u.file_type], fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, letterSpacing: '0.06em' }}>
                    {FILE_TYPE_LABEL[u.file_type] || u.file_type}
                  </span>
                  <div>
                    <div style={{ color: '#ccc', fontSize: 13 }}>{u.file_name}</div>
                    <div style={{ color: '#444', fontSize: 11, marginTop: 2, fontFamily: "'DM Mono', monospace" }}>
                      {u.rows_count?.toLocaleString()} rânduri · {u.status} · {new Date(u.uploaded_at).toLocaleString('ro-RO')}
                    </div>
                  </div>
                </div>
                <button onClick={() => deleteUpload(u.id)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: 4 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Clustering Suggestion */}
      {uploads.length > 0 && (
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: suggestion ? 20 : 0 }}>
            <div>
              <div style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>Clustering inteligent AI</div>
              <div style={{ color: '#555', fontSize: 13, marginTop: 4 }}>Lasă Claude să analizeze datele și să sugereze cea mai bună segmentare.</div>
            </div>
            <button onClick={suggestClustering} disabled={aiLoading} style={{
              ...S.btn(aiLoading ? '#333' : 'linear-gradient(135deg, #8b5cf6, #6366f1)' as any),
              display: 'flex', alignItems: 'center', gap: 8, minWidth: 220,
            }}>
              {aiLoading ? (
                <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #fff4', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Claude analizează datele...</>
              ) : (
                <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"/><path d="M12 8v4l3 3"/></svg>Sugerează clustering cu AI</>
              )}
            </button>
          </div>

          {applyMsg && (
            <div style={{ background: applyMsg.startsWith('✓') ? '#0a1f0a' : '#1f0a0a', border: `1px solid ${applyMsg.startsWith('✓') ? '#1a4a1a' : '#4a1a1a'}`, borderRadius: 8, padding: '10px 14px', color: applyMsg.startsWith('✓') ? '#4ade80' : '#f87171', fontSize: 13, marginTop: 16 }}>{applyMsg}</div>
          )}

          {suggestion && editableSuggestion && (
            <div>
              <div style={{ height: 1, background: '#1a1a25', margin: '0 0 18px' }} />
              <div style={{ color: '#a78bfa', fontSize: 14, fontWeight: 600, marginBottom: 16, lineHeight: 1.5 }}>
                Sugestie AI — {suggestion.reasoning}
              </div>

              {suggestion.recommended_columns?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={S.label}>Coloane recomandate</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                    {suggestion.recommended_columns.map((col: string) => (
                      <span key={col} style={{ background: '#1a1a2e', border: '1px solid #4f46e5', color: '#a78bfa', fontSize: 12, fontWeight: 500, padding: '3px 10px', borderRadius: 6 }}>
                        {col}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Editable min_clients_threshold */}
              <div style={{ marginBottom: 16 }}>
                <div style={S.label}>Prag minim clienți (min_clients_threshold)</div>
                <input
                  type="number" min={1}
                  style={{ ...S.input, width: 160 }}
                  value={editableSuggestion.min_clients_threshold ?? ''}
                  onChange={e => setEditableSuggestion((prev: any) => ({
                    ...prev, min_clients_threshold: parseInt(e.target.value) || 0,
                  }))}
                />
              </div>

              {/* Editable segments */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={S.label}>Segmente sugerate</div>
                  <button
                    onClick={() => setShowAddSeg(v => !v)}
                    style={{ ...S.btn('#1e2a45' as any), fontSize: 12, padding: '5px 12px' }}
                  >
                    + Adaugă segment manual
                  </button>
                </div>

                {showAddSeg && (
                  <div style={{ background: '#0d0d1a', border: '1px solid #2a2a3e', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                      <div>
                        <div style={S.label}>Nume segment</div>
                        <input style={S.input} placeholder="ex: Brutărie Mică" value={newSeg.name}
                          onChange={e => setNewSeg(p => ({ ...p, name: e.target.value }))} />
                      </div>
                      <div>
                        <div style={S.label}>{editableSuggestion.recommended_columns?.[0] || 'Coloana 1'}</div>
                        {colValues[editableSuggestion.recommended_columns?.[0]]?.length > 0 ? (
                          <select style={S.input} value={newSeg.col1} onChange={e => setNewSeg(p => ({ ...p, col1: e.target.value }))}>
                            <option value="">-- Alege valoare --</option>
                            {colValues[editableSuggestion.recommended_columns[0]].map(v => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                          </select>
                        ) : (
                          <input style={S.input}
                            placeholder={`ex: ${editableSuggestion.recommended_columns?.[0] || 'valoare'}`}
                            value={newSeg.col1}
                            onChange={e => setNewSeg(p => ({ ...p, col1: e.target.value }))} />
                        )}
                      </div>
                      <div>
                        <div style={S.label}>{editableSuggestion.recommended_columns?.[1] || 'Coloana 2'}</div>
                        {colValues[editableSuggestion.recommended_columns?.[1]]?.length > 0 ? (
                          <select style={S.input} value={newSeg.col2} onChange={e => setNewSeg(p => ({ ...p, col2: e.target.value }))}>
                            <option value="">-- Alege valoare --</option>
                            {colValues[editableSuggestion.recommended_columns[1]].map(v => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                          </select>
                        ) : (
                          <input style={S.input}
                            placeholder={`ex: ${editableSuggestion.recommended_columns?.[1] || 'valoare'}`}
                            value={newSeg.col2}
                            onChange={e => setNewSeg(p => ({ ...p, col2: e.target.value }))} />
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={addSegment} disabled={!newSeg.name.trim()} style={S.btn('#10b981')}>Adaugă</button>
                      <button onClick={() => { setShowAddSeg(false); setNewSeg({ name: '', col1: '', col2: '' }); }} style={S.btnGhost}>Anulează</button>
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gap: 8 }}>
                  {editableSuggestion.top_segments.map((seg: any, i: number) => (
                    <div key={i} style={{ background: '#0a0a0f', border: '1px solid #1a1a25', borderRadius: 8, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: seg.business_insight ? 4 : 0 }}>
                        <span style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>{seg.name}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ color: '#6366f1', fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
                            {seg.estimated_clients?.toLocaleString()} clienți
                          </span>
                          <button onClick={() => deleteSegment(i)} title="Elimină segment"
                            style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                      {seg.business_insight && <div style={{ color: '#666', fontSize: 12 }}>{seg.business_insight}</div>}
                    </div>
                  ))}
                </div>
              </div>

              {suggestion.coverage_statistics?.coverage_percentage !== undefined && (
                <div style={{ marginBottom: 20 }}>
                  <div style={S.label}>Acoperire totală</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, height: 6, background: '#1a1a25', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', width: `${Math.min(100, suggestion.coverage_statistics.coverage_percentage)}%`, borderRadius: 3 }} />
                    </div>
                    <span style={{ color: '#a78bfa', fontSize: 14, fontWeight: 600, fontFamily: "'DM Mono', monospace", minWidth: 44 }}>
                      {suggestion.coverage_statistics.coverage_percentage}%
                    </span>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={applyClusterConfig} disabled={applyingCluster} style={{
                  ...S.btn(applyingCluster ? '#333' : 'linear-gradient(135deg, #10b981, #059669)' as any),
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  {applyingCluster ? (
                    <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #fff4', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Se aplică...</>
                  ) : '✓ Aplică această configurație'}
                </button>
                <button onClick={() => { setSuggestion(null); setEditableSuggestion(null); setApplyMsg(''); }} style={S.btnGhost}>
                  Ignoră
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Calculate button */}
      {uploads.length > 0 && (
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: calcResult ? 20 : 0 }}>
            <div>
              <div style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>Calculează RV + GP + Matrice</div>
              <div style={{ color: appliedConfig ? '#4ade80' : '#555', fontSize: 13, marginTop: 4 }}>
                {appliedConfig
                  ? `✓ Configurație activă: ${appliedConfig.recommended_columns?.join(' × ')} — ${appliedConfig.top_segments?.length} segmente`
                  : 'Aplică mai întâi configurația AI pentru a activa calculul.'}
              </div>
            </div>
            <button
              onClick={calculate}
              disabled={calculating || !appliedConfig}
              style={{
                ...S.btn((calculating || !appliedConfig) ? '#2a2a35' : 'linear-gradient(135deg, #6366f1, #8b5cf6)' as any),
                display: 'flex', alignItems: 'center', gap: 8, minWidth: 140,
                opacity: !appliedConfig ? 0.5 : 1, cursor: !appliedConfig ? 'not-allowed' : 'pointer',
              }}
            >
              {calculating ? (
                <><span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #fff4', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Se calculează...</>
              ) : (
                <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>Calculează</>
              )}
            </button>
          </div>

          {calcResult && (
            <div>
              <div style={{ height: 1, background: '#1a1a25', margin: '0 0 16px' }} />
              <div style={{ color: '#4ade80', fontSize: 13, fontWeight: 500, marginBottom: 14 }}>
                ✓ {calcResult.message}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                {[
                  { label: 'Clienți procesați', value: calcResult.total_clients },
                  { label: 'Articole procesate', value: calcResult.total_articles },
                  { label: 'Categorii client', value: calcResult.categories?.length },
                ].map(s => (
                  <div key={s.label} style={{ background: '#0a0a0f', borderRadius: 8, padding: '12px 16px' }}>
                    <div style={{ color: '#444', fontSize: 11, marginBottom: 4 }}>{s.label}</div>
                    <div style={{ color: '#fff', fontSize: 22, fontWeight: 600 }}>{s.value}</div>
                  </div>
                ))}
              </div>
              {calcResult.categories?.map((cat: any) => (
                <div key={cat.category} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#0a0a0f', borderRadius: 6, marginBottom: 4 }}>
                  <span style={{ color: '#ccc', fontSize: 13 }}>{cat.category}</span>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <span style={{ color: '#6366f1', fontSize: 12 }}>{cat.total_clients} clienți</span>
                    <span style={{ color: '#10b981', fontSize: 12 }}>{cat.p1_opportunities} oportunități P1</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── USERS TAB ────────────────────────────────────────────────────────────────
function UsersTab({ companyId }: { companyId: number | null }) {
  const api = useApi();
  const { user } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [form, setForm] = useState({ email: '', password: '', full_name: '', role: 'agent', agent_code: '' });
  const [msg, setMsg] = useState(''); const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/admin/users').then(r => setUsers(r.data)).catch(() => {}); }, [api]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault(); setMsg(''); setLoading(true);
    try {
      await api.post('/admin/users', { ...form, company_id: companyId });
      setMsg('✓ Utilizator creat cu succes!');
      setForm({ email: '', password: '', full_name: '', role: 'agent', agent_code: '' });
      api.get('/admin/users').then(r => setUsers(r.data));
    } catch (err: any) { setMsg(err.response?.data?.detail || 'Eroare'); }
    finally { setLoading(false); }
  };

  const deleteUser = async (target: any) => {
    if (!window.confirm(`Ștergi utilizatorul "${target.full_name || target.email}"?`)) return;
    setMsg('');
    try {
      await api.delete(`/admin/users/${target.id}`);
      setMsg('✓ Utilizator șters.');
      api.get('/admin/users').then(r => setUsers(r.data));
    } catch (err: any) {
      setMsg(err.response?.data?.detail || 'Eroare la ștergere');
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 20 }}>
      <div style={S.card}>
        <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Utilizatori activi ({users.length})</div>
        {users.map(u => (
          <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#0a0a0f', borderRadius: 8, marginBottom: 6, border: '1px solid #1a1a25' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: u.role === 'admin' ? '#2a1a0a' : '#0a1a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: u.role === 'admin' ? '#f59e0b' : '#6366f1', fontSize: 12, fontWeight: 700 }}>
                {u.full_name?.charAt(0) || u.email.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>{u.full_name || u.email}</div>
                <div style={{ color: '#444', fontSize: 11, fontFamily: "'DM Mono', monospace" }}>{u.email}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ background: u.role === 'admin' ? '#2a1a0a' : '#0a1a2a', color: u.role === 'admin' ? '#f59e0b' : '#6366f1', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
                {u.role.toUpperCase()}
              </span>
              {u.id !== user?.id && (
                <button
                  onClick={() => deleteUser(u)}
                  title="Șterge utilizator"
                  style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '2px 4px', display: 'flex', alignItems: 'center' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <div style={S.card}>
        <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Adaugă utilizator nou</div>
        <form onSubmit={create}>
          {[
            { key: 'full_name', label: 'Nume complet', placeholder: 'Ion Popescu' },
            { key: 'email', label: 'Email companie', placeholder: 'ion@companie.ro', type: 'email' },
            { key: 'password', label: 'Parolă', placeholder: '••••••••', type: 'password' },
            { key: 'agent_code', label: 'Cod agent (opțional)', placeholder: 'ex: V198' },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 12 }}>
              <label style={S.label}>{f.label}</label>
              <input style={S.input} type={f.type || 'text'} placeholder={f.placeholder}
                value={(form as any)[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                required={f.key !== 'agent_code'} />
            </div>
          ))}
          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Rol</label>
            <select style={{ ...S.input }} value={form.role} onChange={e => setForm(prev => ({ ...prev, role: e.target.value }))}>
              <option value="top_management">Top management</option>
              <option value="manager">Manager</option>
              <option value="agent">Agent</option>
            </select>
          </div>
          {msg && <div style={{ color: msg.startsWith('✓') ? '#4ade80' : '#f87171', fontSize: 12, marginBottom: 12 }}>{msg}</div>}
          <button type="submit" disabled={loading} style={S.btn()}>{loading ? 'Se creează...' : 'Creează utilizator'}</button>
        </form>
      </div>
    </div>
  );
}

// ─── SUMMARY TAB ──────────────────────────────────────────────────────────────
function SummaryTab({ companyId, summaryData, setSummaryData, summaryLoaded, setSummaryLoaded }: {
  companyId: number | null;
  summaryData: any;
  setSummaryData: (v: any) => void;
  summaryLoaded: boolean;
  setSummaryLoaded: (v: boolean) => void;
}) {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [matrixData, setMatrixData] = useState<any[]>([]);

  useEffect(() => {
    if (!companyId || summaryLoaded) return;
    setLoading(true);
    api.get(`/admin/summary?company_id=${companyId}`)
      .then(r => { setSummaryData(r.data); setSummaryLoaded(true); setLoading(false); })
      .catch(() => setLoading(false));
  }, [companyId, summaryLoaded]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    api.get(`/admin/matrix-stats?company_id=${companyId}`)
      .then(r => setMatrixData(r.data))
      .catch(() => {});
  }, [companyId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const data = summaryData;

  if (!companyId) return <div style={{ ...S.card, textAlign: 'center', padding: 60, color: '#444' }}>Selectează o companie.</div>;
  if (loading) return <div style={{ ...S.card, textAlign: 'center', padding: 60, color: '#444' }}>Se încarcă...</div>;
  if (!data?.has_data) return <div style={{ ...S.card, textAlign: 'center', padding: 60, color: '#444' }}>Nu există date calculate pentru această companie.</div>;

  const PRIORITY_COLORS_MAP: Record<string, string> = { P1: '#3C3489', P2: '#534AB7', P3: '#7F77DD', P4: '#AFA9EC', P5: '#065a60', P6: '#1a936f', P7: '#c9a227', P8: '#e63946', P9: '#f4a261' };
  const fmtRON = (v: number | null | undefined) => v != null ? new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(v) + ' RON' : '—';

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {/* Ultimul calcul */}
      <div style={{ ...S.card }}>
        <div style={{ color: '#555', fontSize: 12, marginBottom: 4 }}>Ultimul calcul</div>
        <div style={{ color: '#fff', fontSize: 14 }}>{data.last_calculated ? new Date(data.last_calculated).toLocaleString('ro-RO') : '—'}</div>
      </div>

      {/* Sumar companie */}
      <div style={S.card}>
        <div style={{ color: '#fff', fontSize: 11, fontWeight: 600, marginBottom: 14, letterSpacing: '0.05em', textTransform: 'uppercase' as const }}>Sumar companie</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { label: 'Clienți', value: data.total_clients?.toLocaleString('ro-RO') ?? '—' },
            { label: 'Articole', value: data.total_articles?.toLocaleString('ro-RO') ?? '—' },
            { label: 'Vânzări totale', value: fmtRON(data.total_sales) },
          ].map(s => (
            <div key={s.label} style={{ background: '#0a0a0f', borderRadius: 8, padding: '12px 16px' }}>
              <div style={{ color: '#444', fontSize: 11, marginBottom: 4 }}>{s.label}</div>
              <div style={{ color: '#fff', fontSize: 20, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Potențial nevalorificat */}
      <div style={S.card}>
        <div style={{ color: '#fff', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' as const, marginBottom: 14 }}>Potențial nevalorificat</div>
        <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' as const }}>
          <div>
            <div style={{ color: '#444', fontSize: 11, marginBottom: 3 }}>Total oportunități</div>
            <div style={{ color: '#a78bfa', fontSize: 22, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>
              {data.total_potential_articles != null ? data.total_potential_articles.toLocaleString('ro-RO') : '—'} posibilități
            </div>
          </div>
          <div>
            <div style={{ color: '#444', fontSize: 11, marginBottom: 3 }}>Valoare estimată</div>
            <div style={{ color: '#4ade80', fontSize: 22, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>
              {fmtRON(data.total_potential_value)}
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          {(data.priority_stats || []).map((d: any, idx: number) => {
            const color = PRIORITY_COLORS_MAP[d.priority] || '#666';
            return (
              <div key={d.priority} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 10px', background: '#0a0a0f', borderRadius: 6, borderLeft: `3px solid ${color}` }}>
                <span style={{ color, fontSize: 12, fontWeight: 700, fontFamily: "'DM Mono', monospace", minWidth: 28 }}>{d.priority}</span>
                <span style={{ color: '#ccc', fontSize: 13, fontFamily: "'DM Mono', monospace", minWidth: 80 }}>
                  {d.opportunities != null ? `${Number(d.opportunities).toLocaleString('ro-RO')} oport.` : '—'}
                </span>
                <span style={{ color: '#4ade80', fontSize: 13, fontFamily: "'DM Mono', monospace", minWidth: 120 }}>
                  {fmtRON(d.estimated_value)}
                </span>
                {idx === 0 && <span style={{ color: '#555', fontSize: 11 }}>← cel mai prioritar</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Card explicativ matrice */}
      <div style={{ ...S.card, background: '#0d0d1a', border: '1px solid #2a2a45' }}>
        <div style={{ color: '#a78bfa', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>ℹ️ Cum se citește această matrice?</div>
        <div style={{ color: '#666', fontSize: 13, lineHeight: 1.6 }}>
          Fiecare articol este evaluat independent în fiecare segment de clienți.
          Un articol poate apărea în priorități diferite pentru segmente diferite,
          ceea ce explică de ce totalul din matrice poate depăși numărul de articole unice.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
        <div style={S.card}>
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Distribuție priorități</div>
          {(() => {
            const stats = data.priority_stats || [];
            const maxOpp = Math.max(1, ...stats.map((p: any) => p.opportunities));
            return stats.map((p: any) => (
              <div key={p.priority} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                  <span style={{ color: PRIORITY_COLORS_MAP[p.priority] || '#ccc', fontSize: 13, fontWeight: 600 }}>{p.priority}</span>
                  <span style={{ color: '#ccc', fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
                    {Number(p.opportunities).toLocaleString('ro-RO')} oportunități · {fmtRON(p.estimated_value)}
                  </span>
                </div>
                <div style={{ height: 6, background: '#1a1a25', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: PRIORITY_COLORS_MAP[p.priority] || '#444', width: `${Math.min(100, (p.opportunities / maxOpp) * 100)}%`, borderRadius: 3 }} />
                </div>
              </div>
            ));
          })()}
        </div>

        <div style={S.card}>
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Distribuție clienți per RV</div>
          {data.rv_stats?.map((r: any) => (
            <div key={r.rv} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#0a0a0f', borderRadius: 6, marginBottom: 6 }}>
              <span style={{ color: '#ccc', fontSize: 13, fontWeight: 500 }}>RV{r.rv}</span>
              <div style={{ display: 'flex', gap: 16 }}>
                <span style={{ color: '#6366f1', fontSize: 13, fontFamily: "'DM Mono', monospace" }}>{r.clients} clienți</span>
                <span style={{ color: '#444', fontSize: 13, fontFamily: "'DM Mono', monospace" }}>
                  {r.total_sales ? new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(r.total_sales) + ' RON' : '—'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Matrice P1-P9 — Articole vândute */}
      <div style={S.card}>
        <div style={{ color: '#fff', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Matrice P1-P9 — Articole vândute</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2a2a45' }}>
              {['Prioritate', 'Articole', 'Clienți cumpărători', 'Valoare vândută', 'Penetrare medie'].map(h => (
                <th key={h} style={{ color: '#555', fontWeight: 600, padding: '6px 12px', textAlign: 'left' as const, fontSize: 11, letterSpacing: '0.05em', textTransform: 'uppercase' as const }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrixData.map((row: any) => {
              const color = PRIORITY_COLORS_MAP[row.priority] || '#666';
              return (
                <tr key={row.priority} style={{ background: `${color}18`, borderBottom: '1px solid #1a1a25' }}>
                  <td style={{ padding: '10px 12px', borderLeft: `3px solid ${color}` }}>
                    <span style={{ color, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{row.priority}</span>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#ccc', fontFamily: "'DM Mono', monospace" }}>{row.total_articles?.toLocaleString('ro-RO') ?? '—'}</td>
                  <td style={{ padding: '10px 12px', color: '#ccc', fontFamily: "'DM Mono', monospace" }}>{row.buyers?.toLocaleString('ro-RO') ?? '—'}</td>
                  <td style={{ padding: '10px 12px', color: '#4ade80', fontFamily: "'DM Mono', monospace" }}>{fmtRON(row.total_value)}</td>
                  <td style={{ padding: '10px 12px', color: '#a78bfa', fontFamily: "'DM Mono', monospace" }}>{row.avg_penetration != null ? `${Number(row.avg_penetration).toFixed(1)}%` : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── SUPER ADMIN ─────────────────────────────────────────────────────────────
function SuperAdminPanel() {
  const { user, logout } = useAuth();
  const api = useApi();
  const changePassword = usePasswordChange();
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: '', slug: '', allowed_domain: '', is_active: true });
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({
    name: '',
    slug: '',
    allowed_domain: '',
    admin_email: '',
    admin_password: '',
    admin_full_name: '',
  });

  const loadCompanies = useCallback(() => {
    setLoading(true);
    api.get('/super-admin/companies')
      .then(r => { setCompanies(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [api]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const createCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setMsg('');
    try {
      await api.post('/super-admin/companies', form);
      setForm({ name: '', slug: '', allowed_domain: '', admin_email: '', admin_password: '', admin_full_name: '' });
      setMsg('✓ Companie și admin create.');
      loadCompanies();
    } catch (err: any) {
      setMsg(err.response?.data?.detail || 'Eroare la creare');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (company: any) => {
    setEditingId(company.id);
    setEditForm({
      name: company.name || '',
      slug: company.slug || '',
      allowed_domain: company.allowed_domain || '',
      is_active: company.is_active !== false,
    });
    setMsg('');
  };

  const saveEdit = async (companyId: number) => {
    setMsg('');
    try {
      await api.patch(`/super-admin/companies/${companyId}`, editForm);
      setEditingId(null);
      setMsg('✓ Companie actualizată.');
      loadCompanies();
    } catch (err: any) {
      setMsg(err.response?.data?.detail || 'Eroare la actualizare');
    }
  };

  const toggleCompany = async (company: any) => {
    const nextActive = company.is_active === false;
    const action = nextActive ? 'reactivezi' : 'pui pauză pe';
    if (!window.confirm(`Sigur vrei să ${action} compania "${company.name}"?`)) return;
    setMsg('');
    try {
      await api.patch(`/super-admin/companies/${company.id}`, {
        name: company.name,
        slug: company.slug,
        allowed_domain: company.allowed_domain,
        is_active: nextActive,
      });
      setMsg(nextActive ? '✓ Companie reactivată.' : '✓ Companie pusă pe pauză.');
      loadCompanies();
    } catch (err: any) {
      setMsg(err.response?.data?.detail || 'Eroare la schimbarea statusului');
    }
  };

  const deleteCompany = async (company: any) => {
    if (!window.confirm(`Ștergi definitiv compania "${company.name}" și toate datele asociate?`)) return;
    if (!window.confirm('Confirmă încă o dată ștergerea definitivă. Această acțiune nu poate fi anulată.')) return;
    setMsg('');
    try {
      await api.delete(`/super-admin/companies/${company.id}`);
      setMsg('✓ Companie ștearsă.');
      loadCompanies();
    } catch (err: any) {
      setMsg(err.response?.data?.detail || 'Eroare la ștergere');
    }
  };

  const fmtDate = (v: string | null) => v ? new Date(v).toLocaleString('ro-RO') : '—';

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <div style={{ borderBottom: '1px solid #141420', padding: '0 32px', display: 'flex', alignItems: 'center', height: 54 }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 28, height: 28, background: 'linear-gradient(135deg, #f97316, #ef4444)', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"/></svg>
          </div>
          <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>Super Admin</span>
          <span style={{ color: '#333' }}>·</span>
          <span style={{ color: '#555', fontSize: 13 }}>Sales Intelligence Master</span>
        </div>
        <span style={{ color: '#444', fontSize: 13, marginRight: 12 }}>{user?.email}</span>
        <button onClick={changePassword} style={S.btnGhost}>Parolă</button>
        <button onClick={logout} style={S.btnGhost}>Ieși</button>
      </div>

      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '30px 32px', display: 'grid', gridTemplateColumns: '1.25fr 0.85fr', gap: 24 }}>
        <div style={S.card}>
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 700, marginBottom: 18 }}>Companii</div>
          {loading ? (
            <div style={{ color: '#444', padding: 40, textAlign: 'center' }}>Se încarcă...</div>
          ) : companies.length === 0 ? (
            <div style={{ color: '#444', padding: 40, textAlign: 'center' }}>Nicio companie creată încă.</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {companies.map(c => (
                <div key={c.id} style={{ background: '#0a0a0f', border: '1px solid #1a1a25', borderRadius: 8, padding: '14px 16px' }}>
                  {editingId === c.id ? (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr 1fr auto', gap: 10, alignItems: 'end' }}>
                        <div>
                          <label style={S.label}>Denumire</label>
                          <input style={S.input} value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
                        </div>
                        <div>
                          <label style={S.label}>Slug</label>
                          <input style={S.input} value={editForm.slug} onChange={e => setEditForm(p => ({ ...p, slug: e.target.value }))} />
                        </div>
                        <div>
                          <label style={S.label}>Domeniu</label>
                          <input style={S.input} value={editForm.allowed_domain} onChange={e => setEditForm(p => ({ ...p, allowed_domain: e.target.value }))} />
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888', fontSize: 12, paddingBottom: 9 }}>
                          <input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm(p => ({ ...p, is_active: e.target.checked }))} />
                          activă
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => saveEdit(c.id)} style={S.btn('#10b981')}>Salvează</button>
                        <button onClick={() => setEditingId(null)} style={S.btnGhost}>Anulează</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>{c.name}</div>
                            <span style={{
                              color: c.is_active === false ? '#f59e0b' : '#4ade80',
                              background: c.is_active === false ? '#2a1a0a' : '#0a1f0a',
                              border: `1px solid ${c.is_active === false ? '#5a3a0a' : '#1a4a1a'}`,
                              borderRadius: 4,
                              padding: '1px 7px',
                              fontSize: 10,
                              fontWeight: 700,
                            }}>
                              {c.is_active === false ? 'PAUZĂ' : 'ACTIV'}
                            </span>
                          </div>
                          <div style={{ color: '#555', fontSize: 12, fontFamily: "'DM Mono', monospace", marginTop: 2 }}>
                            {c.slug} · @{c.allowed_domain || 'neconfigurat'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 12, color: '#888', fontSize: 12, fontFamily: "'DM Mono', monospace", flexWrap: 'wrap' as const, justifyContent: 'flex-end' }}>
                          <span>{c.users_count || 0} useri</span>
                          <span>{c.agents_count || 0} agenți</span>
                          <span>{c.total_clients || 0} clienți</span>
                          <span>{c.total_articles || 0} articole</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 10 }}>
                        <div style={{ color: '#444', fontSize: 11 }}>Ultimul calcul: {fmtDate(c.last_calculated_at)}</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => startEdit(c)} style={{ ...S.btnGhost, padding: '5px 10px', fontSize: 12 }}>Editează</button>
                          <button onClick={() => toggleCompany(c)} style={{ ...S.btnGhost, padding: '5px 10px', fontSize: 12, color: c.is_active === false ? '#4ade80' : '#f59e0b' }}>
                            {c.is_active === false ? 'Reactivează' : 'Pauză'}
                          </button>
                          <button onClick={() => deleteCompany(c)} style={{ ...S.btnGhost, padding: '5px 10px', fontSize: 12, color: '#f87171' }}>Șterge</button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={S.card}>
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 700, marginBottom: 18 }}>Creează companie</div>
          <form onSubmit={createCompany}>
            {[
              { key: 'name', label: 'Denumire companie', placeholder: 'Temad SRL' },
              { key: 'slug', label: 'Slug', placeholder: 'temad' },
              { key: 'allowed_domain', label: 'Domeniu permis', placeholder: 'temad.ro' },
              { key: 'admin_full_name', label: 'Nume admin', placeholder: 'Admin Temad' },
              { key: 'admin_email', label: 'Email admin', placeholder: 'admin@temad.ro', type: 'email' },
              { key: 'admin_password', label: 'Parolă admin', placeholder: '••••••••', type: 'password' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 12 }}>
                <label style={S.label}>{f.label}</label>
                <input
                  style={S.input}
                  type={f.type || 'text'}
                  value={(form as any)[f.key]}
                  placeholder={f.placeholder}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  required
                />
              </div>
            ))}
            {msg && <div style={{ color: msg.startsWith('✓') ? '#4ade80' : '#f87171', fontSize: 12, marginBottom: 12 }}>{msg}</div>}
            <button type="submit" disabled={creating} style={S.btn('linear-gradient(135deg, #f97316, #ef4444)' as any)}>
              {creating ? 'Se creează...' : 'Creează companie + admin'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ADMIN LAYOUT ────────────────────────────────────────────────────────
function AdminPanel() {
  const { user, logout } = useAuth();
  const changePassword = usePasswordChange();
  const [activeTab, setActiveTab] = useState('company');
  const companyId = user?.company_id ?? null;
  const [suggestion, setSuggestion] = useState<any>(null);
  const [appliedConfig, setAppliedConfig] = useState<any>(null);
  const [calcResult, setCalcResult] = useState<any>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [summaryLoaded, setSummaryLoaded] = useState(false);

  useEffect(() => {
    if (calcResult) setSummaryLoaded(false);
  }, [calcResult]);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', fontFamily: "'DM Sans', sans-serif", display: 'flex', flexDirection: 'column' }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />

      {/* Top bar */}
      <div style={{ borderBottom: '1px solid #141420', padding: '0 32px', display: 'flex', alignItems: 'center', height: 52, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <div style={{ width: 26, height: 26, background: 'linear-gradient(135deg, #f59e0b, #ef4444)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
          </div>
          <span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>Admin Panel</span>
          <span style={{ color: '#333', fontSize: 14 }}>·</span>
          <span style={{ color: '#555', fontSize: 13 }}>Sales Intelligence</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#444', fontSize: 13 }}>{user?.email}</span>
          <button onClick={changePassword} style={S.btnGhost}>Parolă</button>
          <button onClick={logout} style={S.btnGhost}>Ieși</button>
        </div>
      </div>

      {/* Tab nav */}
      <div style={{ borderBottom: '1px solid #141420', padding: '0 32px', display: 'flex', gap: 4 }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            background: 'none', border: 'none', borderBottom: `2px solid ${activeTab === tab.id ? '#f59e0b' : 'transparent'}`,
            padding: '12px 16px', color: activeTab === tab.id ? '#fff' : '#555',
            fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400, cursor: 'pointer',
            fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 7, transition: 'color 0.15s',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d={tab.icon}/></svg>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '28px 32px', maxWidth: 1000, width: '100%', margin: '0 auto', boxSizing: 'border-box' as const }}>
        {activeTab === 'company' && <CompanyTab />}
        {activeTab === 'uploads' && (
          <UploadsTab
            companyId={companyId}
            suggestion={suggestion} setSuggestion={setSuggestion}
            appliedConfig={appliedConfig} setAppliedConfig={setAppliedConfig}
            calcResult={calcResult} setCalcResult={setCalcResult}
            aiLoading={aiLoading} setAiLoading={setAiLoading}
          />
        )}
        {activeTab === 'users' && <UsersTab companyId={companyId} />}
        {activeTab === 'summary' && (
          <SummaryTab
            companyId={companyId}
            summaryData={summaryData}
            setSummaryData={setSummaryData}
            summaryLoaded={summaryLoaded}
            setSummaryLoaded={setSummaryLoaded}
          />
        )}
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
function AppInner() {
  const { user, logout } = useAuth();
  const superPath = isSuperAdminPath();

  useEffect(() => {
    if (!user) return;
    if (user.role === 'super_admin' && !superPath) logout();
    if (user.role === 'admin' && superPath) logout();
  }, [user, superPath, logout]);

  if (!user) return <LoginPage />;
  if (user.role === 'super_admin') {
    return superPath ? <SuperAdminPanel /> : <LoginPage />;
  }
  if (superPath) return <LoginPage />;
  return <AdminPanel />;
}
